import {Component, Input, OnInit} from '@angular/core';
import {SearchProductEntry, ProductQuery} from '@services/resource/search-product-resources';
import {ProductStatus} from '@services/model/product';
import {VoteService} from '@services/vote.service';
import {UUID} from '@util/types';
import {PrepareUpdateService} from '@services/prepare-update-service';
import {StatusResource} from '@services/resource/status-resource';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Option, isSome, some, none} from 'fp-ts/lib/Option';
import {VoilaUserService} from '@services/voila-user.service';
import {MarkantElementsModule} from '@markant/elements-angular';
import {MatIcon} from '@angular/material/icon';
import {MinPrivilegeDirective} from '@util/directives/min-privilege.directive';
import {CommonModule} from '@angular/common';
import {OptionPipe} from '@util/pipes/option-pipe';

@Component({
  selector: 'app-ma-voting-widget',
  templateUrl: './ma-voting-widget.component.html',
  styleUrls: ['./ma-voting-widget.component.scss'],
  standalone: true,
  imports: [CommonModule, MarkantElementsModule, MatIcon, MinPrivilegeDirective, OptionPipe],
})
export class MaVotingWidgetComponent implements OnInit {
  ProductStatus = ProductStatus;
  @Input() entry: SearchProductEntry;
  @Input() query?: ProductQuery;
  color: string;
  voteCount: Option<number> = none;
  preparedStatus?: Option<StatusResource>;

  constructor(
    private readonly voteService: VoteService,
    private readonly prepareUpdateService: PrepareUpdateService,

    readonly userService: VoilaUserService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    if (!this.query) {
      return;
    }
    if (!this.cycleId) {
      return;
    }

    this.voteService.getVotes(this.cycleId, this.productId).subscribe((v) => (this.voteCount = some(v.filter((x) => x.vote.verdict > 0).length)));

    this.prepareUpdateService.getPreparedStatus(this.productId, this.cycleId).subscribe((ps) => (this.preparedStatus = ps));
  }

  get markedForInvite(): boolean {
    if (!this.preparedStatus) {
      return false;
    }
    return isSome(this.preparedStatus) && this.preparedStatus.value.status === ProductStatus.invited;
  }

  get markedForNoInvite(): boolean {
    if (!this.preparedStatus) {
      return false;
    }
    return isSome(this.preparedStatus) && this.preparedStatus.value.status === ProductStatus.notInvited;
  }

  public get cycleId(): UUID | null {
    console.assert(Boolean(this.query), 'query shoud always be set for ma-voting-widget');
    const filter = (this.query as ProductQuery).cycleFilter;
    if (filter === 'none' || filter === 'all') {
      return null;
    }
    return filter;
  }

  private get productId(): UUID {
    return this.entry.product.id as UUID;
  }

  markForInvite(): void {
    if (!this.cycleId) {
      return;
    }

    if (this.markedForInvite) {
      this.color = 'primary';
      this.deletePreparedUpdate();
      return;
    }
    this.prepareUpdateService.setPrepareStatus(this.productId, this.cycleId, ProductStatus.invited).subscribe(() => {
      this.snackBar.open(`Produkt "${this.entry.product.name}" wurde für Einladung vorgemerkt`, 'Schließen', {duration: 5000});
      this.preparedStatus = some(new StatusResource(ProductStatus.invited));
    });
  }

  markForNotInvite(): void {
    if (!this.cycleId) {
      return;
    }
    if (this.markedForNoInvite) {
      this.color = 'secondary';
      this.deletePreparedUpdate();
      return;
    }
    this.prepareUpdateService.setPrepareStatus(this.productId, this.cycleId, ProductStatus.notInvited).subscribe(() => {
      this.snackBar.open(`Produkt "${this.entry.product.name}" wurde für Absage vorgemerkt`, 'Schließen', {duration: 5000});
      this.preparedStatus = some(new StatusResource(ProductStatus.notInvited));
    });
  }

  private deletePreparedUpdate(): void {
    if (!this.cycleId) {
      return;
    }
    this.prepareUpdateService.deletePreparedUpdate(this.productId, this.cycleId).subscribe(() => {
      this.snackBar.open(`Einlade/Absage-Vormerkung für Produkt ${this.entry.product.name}" wurde gelöscht.`, 'Schließen', {duration: 5000});
      this.preparedStatus = none;
    });
  }
}
